import type { ClientType, LegalType } from '@shared/documents'
import type { DocsFormValues } from '@/helpers/docs-form'

type InputMode =
  | 'decimal'
  | 'email'
  | 'none'
  | 'numeric'
  | 'search'
  | 'tel'
  | 'text'
  | 'url'

export type FormFieldConfig = {
  key: keyof DocsFormValues
  label: string
  placeholder?: string
  inputMode?: InputMode
  multiline?: boolean
  options?: readonly string[]
}

export type FormStepConfig = {
  title: string
  description?: string
  fields: readonly FormFieldConfig[]
}

export type PassportOcrPage = 'main' | 'registration'

export type PassportOcrData = Partial<
  Record<keyof DocsFormValues, string | null>
>

export const COMPANY = import.meta.env.BUN_PUBLIC_COMPANY ?? 'default'

export const isMartGlobalCompany = COMPANY === 'mart-global'

export const OCR_FIELDS_BY_PAGE = {
  main: [
    'fullNameRu',
    'fullNameEn',
    'birthDate',
    'passportSeriesNumber',
    'passportIssuedByRu',
    'passportIssuedByEn',
    'passportIssueDate',
    'passportDivisionCode',
  ],
  registration: ['regAddressRu', 'regAddressEn'],
} as const satisfies Record<PassportOcrPage, readonly (keyof DocsFormValues)[]>

export const OCR_SUCCESS_MESSAGE_BY_PAGE: Record<PassportOcrPage, string> = {
  main: 'Данные основного разворота паспорта добавлены в форму',
  registration: 'Данные адреса регистрации добавлены в форму',
}

export const SUPPLIER_FIELD_KEYS = [
  'supplierName',
  'supplierAddress',
  'supplierAccount',
  'supplierBank',
  'supplierBankAddress',
  'supplierBankBranch',
  'supplierBankCode',
  'correspBank',
  'correspBankAddress',
  'correspBankCode',
  'correspBankAccount',
] as const satisfies readonly (keyof DocsFormValues)[]

export type SupplierFieldKey = (typeof SUPPLIER_FIELD_KEYS)[number]

export const STEP_CONFIGS: readonly FormStepConfig[][] = [
  [
    {
      title: 'Информация о клиенте',
      fields: [
        {
          key: 'fullNameRu',
          label: 'ФИО (RU)',
          placeholder: 'Иванов Иван Иванович',
        },
        {
          key: 'fullNameEn',
          label: 'Full name (EN)',
          placeholder: 'Ivanov Ivan Ivanovich',
        },
        {
          key: 'birthDate',
          label: 'Дата рождения',
          placeholder: 'ДД.ММ.ГГГГ',
          inputMode: 'numeric',
        },
      ],
    },
    {
      title: 'Паспортные данные',
      fields: [
        {
          key: 'passportSeriesNumber',
          label: 'Серия и номер паспорта',
          placeholder: '45 10 123456',
        },
        {
          key: 'passportIssuedByRu',
          label: 'Кем выдан (RU)',
          placeholder: 'ОВД района Тверской г. Москвы',
          multiline: true,
        },
        {
          key: 'passportIssuedByEn',
          label: 'Issued by (EN)',
          placeholder: 'Tverskoy District Police Dept., Moscow',
          multiline: true,
        },
        {
          key: 'passportIssueDate',
          label: 'Дата выдачи',
          placeholder: 'ДД.ММ.ГГГГ',
          inputMode: 'numeric',
        },
        {
          key: 'passportDivisionCode',
          label: 'Код подразделения',
          placeholder: '770-001',
        },
      ],
    },
    {
      title: 'Адрес регистрации',
      fields: [
        {
          key: 'regAddressRu',
          label: 'Адрес регистрации (RU)',
          placeholder: 'г. Москва, ул. Тверская, д. 1',
          multiline: true,
        },
        {
          key: 'regAddressEn',
          label: 'Address of registration (EN)',
          placeholder: 'Moscow, Tverskaya st., 1',
          multiline: true,
        },
      ],
    },
  ],
  [
    {
      title: 'Поставщик',
      fields: [
        {
          key: 'supplierName',
          label: 'Наименование поставщика',
          placeholder: 'ACME Co., Ltd.',
        },
        {
          key: 'supplierAddress',
          label: 'Адрес поставщика',
          placeholder: '1-2-3 Shibuya, Tokyo, Japan',
          multiline: true,
        },
      ],
    },
    {
      title: 'Банк поставщика',
      fields: [
        {
          key: 'supplierAccount',
          label: 'Счет поставщика',
          placeholder: '1234567890123456',
        },
        {
          key: 'supplierBank',
          label: 'Банк поставщика',
          placeholder: 'MUFG Bank, Ltd.',
        },
        {
          key: 'supplierBankAddress',
          label: 'Адрес банка поставщика',
          placeholder: 'Tokyo, Japan',
          multiline: true,
        },
        {
          key: 'supplierBankBranch',
          label: 'Филиал банка поставщика',
          placeholder: 'Shibuya Branch',
        },
        {
          key: 'supplierBankCode',
          label: 'Код банка поставщика',
          placeholder: '0005',
        },
      ],
    },
  ],
  [
    {
      title: 'Инвойс',
      fields: [
        {
          key: 'invoiceNo',
          label: 'Номер инвойса',
          placeholder: 'TAL 202602262197',
        },
        {
          key: 'productName',
          label: 'Наименование товара',
          placeholder: 'Porsche Macan VINCODE',
        },
        {
          key: 'invoiceDate',
          label: 'Дата инвойса',
          placeholder: 'ДД.ММ.ГГГГ',
          inputMode: 'numeric',
        },
        {
          key: 'invoiceAmount',
          label: 'Сумма инвойса',
          placeholder: '150000',
          inputMode: 'decimal',
        },
        {
          key: 'invoiceCurrency',
          label: 'Валюта',
          options: ['JPY', 'CNY', 'KRW', 'USD', 'EUR', 'AED'],
        },
      ],
    },
    {
      title: 'Итог',
      fields: [
        {
          key: 'amountRub',
          label: 'Сумма в рублях (с вознаграждением)',
          placeholder: '93000',
          inputMode: 'decimal',
        },
        {
          key: 'conversionRate',
          label: 'Курс конвертации',
          placeholder: '0.62',
          inputMode: 'decimal',
        },
      ],
    },
    {
      title: 'Контракт',
      fields: [
        {
          key: 'contractDate',
          label: 'Дата контракта',
          placeholder: 'ДД.ММ.ГГГГ',
          inputMode: 'numeric',
        },
      ],
    },
    {
      title: 'Отчет',
      fields: [
        {
          key: 'reportSignedDate',
          label: 'Дата подписания отчета',
          placeholder: 'ДД.ММ.ГГГГ',
          inputMode: 'numeric',
        },
      ],
    },
  ],
] as const

const LEGAL_IP_STEP: readonly FormStepConfig[] = [
  {
    title: 'Информация о клиенте',
    fields: [
      {
        key: 'fullNameRu',
        label: 'ФИО (RU)',
        placeholder: 'Иванов Иван Иванович',
      },
      {
        key: 'fullNameEn',
        label: 'Full name (EN)',
        placeholder: 'Ivanov Ivan Ivanovich',
      },
    ],
  },
  {
    title: 'Адрес',
    fields: [
      {
        key: 'legalAddressRu',
        label: 'Адрес (RU)',
        placeholder: 'г. Москва, ул. Тверская, д. 1',
        multiline: true,
      },
      {
        key: 'legalAddressEn',
        label: 'Address (EN)',
        placeholder: 'Moscow, Tverskaya st., 1',
        multiline: true,
      },
      {
        key: 'legalActualAddressRu',
        label: 'Фактический адрес (RU)',
        placeholder: 'г. Москва, ул. Тверская, д. 1',
        multiline: true,
      },
      {
        key: 'legalActualAddressEn',
        label: 'Actual address (EN)',
        placeholder: 'Moscow, Tverskaya st., 1',
        multiline: true,
      },
    ],
  },
  {
    title: 'Реквизиты',
    fields: [
      {
        key: 'psrnsp',
        label: 'ОГРНИП (PSRNSP)',
        placeholder: '315774600368190',
      },
      {
        key: 'tin',
        label: 'ИНН (TIN)',
        placeholder: '770123456789',
      },
      {
        key: 'legalBankRu',
        label: 'Банк (RU)',
        placeholder: 'ПАО «Сбербанк»',
        multiline: true,
      },
      {
        key: 'legalBankEn',
        label: 'Bank (EN)',
        placeholder: 'Sberbank',
        multiline: true,
      },
      {
        key: 'legalSettlementAccount',
        label: 'Расчетный счет',
        placeholder: '40802810400000012345',
      },
      {
        key: 'legalCorrespondentAccount',
        label: 'Корреспондентский счет',
        placeholder: '30101810400000000225',
      },
      {
        key: 'bic',
        label: 'БИК (BIC)',
        placeholder: '044525225',
      },
    ],
  },
]

const LEGAL_ORGANIZATION_STEP: readonly FormStepConfig[] = [
  {
    title: 'Информация о клиенте',
    fields: [
      {
        key: 'organizationNameRu',
        label: 'Название организации (RU)',
        placeholder: 'ООО «Ромашка»',
      },
      {
        key: 'organizationNameEn',
        label: 'Organization name (EN)',
        placeholder: 'Romashka LLC',
      },
      {
        key: 'fullNameRu',
        label: 'ФИО представителя (RU)',
        placeholder: 'Иванов Иван Иванович',
      },
      {
        key: 'fullNameEn',
        label: 'Representative full name (EN)',
        placeholder: 'Ivanov Ivan Ivanovich',
      },
      {
        key: 'positionRu',
        label: 'Должность (RU)',
        placeholder: 'директор',
      },
      {
        key: 'positionEn',
        label: 'Position (EN)',
        placeholder: 'Director',
      },
    ],
  },
  {
    title: 'Адрес',
    fields: [
      {
        key: 'legalAddressRu',
        label: 'Адрес (RU)',
        placeholder: 'г. Москва, ул. Тверская, д. 1',
        multiline: true,
      },
      {
        key: 'legalAddressEn',
        label: 'Address (EN)',
        placeholder: 'Moscow, Tverskaya st., 1',
        multiline: true,
      },
      {
        key: 'legalActualAddressRu',
        label: 'Фактический адрес (RU)',
        placeholder: 'г. Москва, ул. Тверская, д. 1',
        multiline: true,
      },
      {
        key: 'legalActualAddressEn',
        label: 'Actual address (EN)',
        placeholder: 'Moscow, Tverskaya st., 1',
        multiline: true,
      },
    ],
  },
  {
    title: 'Реквизиты',
    fields: [
      { key: 'kpp', label: 'КПП', placeholder: '770101001' },
      { key: 'ogrn', label: 'ОГРН', placeholder: '1027700132195' },
      {
        key: 'tin',
        label: 'ИНН (TIN)',
        placeholder: '770123456789',
      },
      {
        key: 'legalBankRu',
        label: 'Банк (RU)',
        placeholder: 'ПАО «Сбербанк»',
        multiline: true,
      },
      {
        key: 'legalBankEn',
        label: 'Bank (EN)',
        placeholder: 'Sberbank',
        multiline: true,
      },
      {
        key: 'legalSettlementAccount',
        label: 'Расчетный счет',
        placeholder: '40802810400000012345',
      },
      {
        key: 'legalCorrespondentAccount',
        label: 'Корреспондентский счет',
        placeholder: '30101810400000000225',
      },
      { key: 'bic', label: 'БИК (BIC)', placeholder: '044525225' },
    ],
  },
]

const MART_GLOBAL_STEP_CONFIGS: readonly FormStepConfig[][] = [
  [
    {
      title: 'Информация о клиенте',
      fields: [
        {
          key: 'fullNameRu',
          label: 'ФИО',
          placeholder: 'Иванов Иван Иванович',
        },
        {
          key: 'birthDate',
          label: 'Дата рождения',
          placeholder: 'ДД.ММ.ГГГГ',
          inputMode: 'numeric',
        },
        {
          key: 'birthPlace',
          label: 'Место рождения',
          placeholder: 'г. Москва',
        },
        {
          key: 'passportSeriesNumber',
          label: 'Паспорт (серия и номер)',
          placeholder: '45 10 123456',
        },
        {
          key: 'regAddressRu',
          label: 'Адрес регистрации',
          placeholder: 'г. Москва, ул. Тверская, д. 1',
          multiline: true,
        },
        {
          key: 'clientPhone',
          label: 'Телефон клиента',
          placeholder: '+7 (999) 123-45-67',
          inputMode: 'tel',
        },
        {
          key: 'clientEmail',
          label: 'Электронная почта клиента',
          placeholder: 'client@example.com',
          inputMode: 'email',
        },
      ],
    },
    {
      title: 'Транспортное средство',
      fields: [
        {
          key: 'vehicleBrandModel',
          label: 'Марка, модель',
          placeholder: 'Toyota Mark II',
        },
        {
          key: 'vinOrBody',
          label: 'VIN / номер кузова',
          placeholder: 'JZX100-0000000',
        },
        {
          key: 'vehicleYear',
          label: 'Год выпуска',
          placeholder: '2000',
          inputMode: 'numeric',
        },
        {
          key: 'purchaseCountry',
          label: 'Страна приобретения',
          placeholder: 'Япония',
        },
        {
          key: 'auctionLot',
          label: 'Номер лота',
          placeholder: '12345',
        },
      ],
    },
  ],
  [
    {
      title: 'Инвойс',
      fields: [
        {
          key: 'invoiceNo',
          label: 'Номер инвойса',
          placeholder: 'TAL 202602262197',
        },
        {
          key: 'productName',
          label: 'Наименование товара',
          placeholder: 'Toyota Mark II',
        },
        {
          key: 'invoiceAmount',
          label: 'Стоимость покупки (валюта)',
          placeholder: '150000',
          inputMode: 'decimal',
        },
        {
          key: 'invoiceCurrency',
          label: 'Валюта',
          options: ['JPY', 'CNY', 'KRW', 'USD', 'EUR', 'AED'],
        },
        {
          key: 'amountRub',
          label: 'Стоимость покупки (рубли)',
          placeholder: '93000',
          inputMode: 'decimal',
        },
        {
          key: 'agentRewardPercent',
          label: 'Вознаграждение агента (%)',
          placeholder: '3',
          inputMode: 'decimal',
        },
        {
          key: 'securityPaymentRub',
          label: 'Обеспечительный платёж (рубли)',
          placeholder: '10000',
          inputMode: 'decimal',
        },
      ],
    },
    {
      title: 'Контракт',
      fields: [
        {
          key: 'contractNo',
          label: 'Номер договора',
          placeholder: 'АГ-001/2026 (или автоматически)',
        },
        {
          key: 'contractDate',
          label: 'Дата контракта',
          placeholder: 'ДД.ММ.ГГГГ',
          inputMode: 'numeric',
        },
      ],
    },
  ],
]

export const getStepConfigs = (
  clientType: ClientType,
  legalType: LegalType = 'ip',
): readonly (readonly FormStepConfig[])[] => {
  if (isMartGlobalCompany) {
    return MART_GLOBAL_STEP_CONFIGS
  }

  if (clientType === 'legal') {
    return [
      legalType === 'organization' ? LEGAL_ORGANIZATION_STEP : LEGAL_IP_STEP,
      STEP_CONFIGS?.[1] ?? [],
      STEP_CONFIGS?.[2] ?? [],
    ] as const
  }

  return STEP_CONFIGS
}
